import { GLManager } from '@play-co/timestep-core/renderer';
import {
  CustomFilter,
  ImageView,
  View,
  ViewOpts,
} from '@play-co/timestep-core/ui';
import ButtonScaleView from 'src/lib/ui/components/ButtonScaleView';
import { Feature } from 'src/replicant/analytics';
import { HueShift, getHueShift } from '../shaders/HueShift';
import bitmapFonts from 'src/lib/bitmapFonts';
import LangBitmapFontTextView from 'src/lib/ui/components/LangBitmapFontTextView';
import {
  CollectionId,
  collectionColors,
} from 'src/replicant/cards/cards.ruleset';
import i18n from 'src/lib/i18n/i18n';
import { cardsOpenPopupCards } from '../cards.sequence';
import { CardsBadge } from './CardsBadge';
import LazyImageView from 'src/lib/ui/components/LazyImageView';
import sounds from 'src/lib/sounds';
import { CardsProgressBar } from './CardsProgressBar';
import { cardsGetCollectionIcon } from '../cards.assets';

export type CardCollectionState = {
  id: CollectionId;
  collected: number;
  totalCards: number;
  newCards?: boolean;
  rewardsAvailable?: boolean;
  color?: string;
  interactive?: boolean;
};

/** Options for a collection icon and its nested Cards popup lifecycle. */
type CardCollectionIconOpts = ViewOpts & {
  onCollectionPopupClose?: () => void | Promise<void>;
};

export class CardCollectionIcon extends View {
  private icon: LazyImageView;
  private iconBackgroundTint: ImageView;
  private content: ButtonScaleView;
  private badge: CardsBadge;
  private text: LangBitmapFontTextView;
  private setColor: (color: string) => void;
  private setCardProgress: (collected: number, totalCards: number) => void;
  private currentState?: CardCollectionState;
  private onCollectionPopupClose?: () => void | Promise<void>;

  constructor(opts: CardCollectionIconOpts) {
    const { onCollectionPopupClose, ...viewOpts } = opts;
    super({
      width: 200,
      height: 252,
      ...viewOpts,
    });
    this.onCollectionPopupClose = onCollectionPopupClose;

    this.content = new ButtonScaleView({
      superview: this,
      width: this.style.width,
      height: this.style.height,
      onClick: async () => this.openCollection(),
      tracking: {
        feature: Feature.CARDS._,
        $subFeature: Feature.CARDS.COLLECTION,
      },
    });

    const shader = GLManager.isSupported
      ? GLManager.createShader(HueShift)
      : null;

    const iconCenter = {
      x: this.style.width / 2,
      y: this.style.height / 2 - 30,
    };

    this.iconBackgroundTint = new ImageView({
      superview: this.content,
      image: 'assets/ui/cards/common/set_bg.png',
      width: 190,
      height: 190,
      centerOnOrigin: true,
      filter: shader ? new CustomFilter(shader) : undefined,
      ...iconCenter,
    });

    this.icon = new LazyImageView({
      superview: this.content,
      width: 160,
      height: 160,
      centerOnOrigin: true,
      ...iconCenter,
    });

    this.badge = new CardsBadge({
      superview: this.content,
      x: 35,
      y: 35,
    });

    const banner = new ImageView({
      superview: this.content,
      image: 'assets/ui/cards/common/banner.png',
      width: 215,
      height: 60,
      centerOnOrigin: true,
      x: this.style.width / 2,
      y: this.style.height - 70,
      filter: shader ? new CustomFilter(shader) : undefined,
    });

    this.text = new LangBitmapFontTextView({
      superview: banner,
      width: banner.style.width,
      height: banner.style.height - 10,
      x: banner.style.width / 2,
      y: banner.style.height / 2 - 5,
      centerOnOrigin: true,
      align: 'center',
      verticalAlign: 'center',
      size: 24,
      font: bitmapFonts('Body'),
      color: '#ffffff',
      localeText: () => 'Neon Party',
      wordWrap: true,
    });

    const horizontalProgress = new CardsProgressBar({
      superview: this,
      width: 230,
      scale: 0.9,
      x: this.style.width / 2,
      y: this.style.height - 20,
      centerOnOrigin: true,
    });

    const checkmark = new ImageView({
      superview: this.content,
      image: 'assets/ui/cards/common/icon_check.png',
      width: 86,
      height: 73,
      x: this.style.width / 2,
      y: this.style.height - 20,
      centerOnOrigin: true,
      scale: 0.5,
    });

    this.setColor = (color: string) => {
      if (!shader) return;
      shader.shift = getHueShift('#DF4242', color);
    };

    this.setCardProgress = (collected: number, totalCards: number) => {
      if (collected === totalCards) {
        checkmark.show();
        horizontalProgress.hide();
        return;
      }
      checkmark.hide();
      horizontalProgress.show();
      horizontalProgress.setProgress(collected, totalCards);
    };
  }

  public setState(state?: CardCollectionState) {
    this.currentState = state;

    if (!state) {
      this.content.hide();
      return;
    }
    this.content.show();

    const color = state.color ?? collectionColors[state.id];
    this.setColor(color);
    this.icon.spinnerColor = color;
    this.icon.updateImage(cardsGetCollectionIcon(state.id));
    this.setCardProgress(state.collected, state.totalCards);
    this.text.localeText = () => i18n(`cards.collections.${state.id}.name`);

    if (state.rewardsAvailable) {
      this.badge.setText(i18n('cards.claim'));
      this.badge.show();
    } else if (state.newCards) {
      this.badge.setText(i18n('cards.new'));
      this.badge.show();
    } else {
      this.badge.hide();
    }
  }

  private async openCollection() {
    const state = this.currentState;
    if (!state?.interactive) return;

    // Hide the badge when the user opens the popup
    this.badge.hide();

    sounds.sfx.play('ok');
    await cardsOpenPopupCards(state.id);

    // Refresh the main Cards popup after the nested collection popup closes.
    await this.onCollectionPopupClose?.();
  }
}
